import { Button, Divider, Flex, Grid, Group, UnstyledButton } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useEffect, useState } from 'react';

import { deleteIcon } from '@assets';
import { AsyncAutocomplete, TextInput } from '@components';

interface FixedFieldState {
  property: string;
  value: string;
}

interface TemplateFixedValueProps {
  form: UseFormReturnType<any>;
  arrayPath: string;
  disabled?: boolean;
  maxRows?: number;
  onChange?: (values: string[], arrayPath: string) => void;
  propertyOptions?: { value: string; label: string }[];
}

const createInitialState = (): FixedFieldState => ({
  property: '',
  value: '',
});

export const TemplateFixedValue: React.FC<TemplateFixedValueProps> = ({
  form,
  arrayPath,
  disabled = false,
  maxRows = 9999,
  onChange,
  propertyOptions = [],
}) => {
  const [arrayPathValue, setArrayPathValue] = useState([]);
  const addRow = () => {
    const array = [...arrayPathValue, createInitialState()];
    setArrayPathValue(array);
    handleChange(array);
  };

  const removeRow = (rowIdx: number) => {
    const array = [...arrayPathValue];
    array.splice(rowIdx, 1);
    setArrayPathValue(array);
    handleChange(array);
  };

  const updateField = (rowIdx: number, updates: Partial<FixedFieldState>) => {
    const array = [...arrayPathValue];
    array[rowIdx] = { ...array[rowIdx], ...updates };
    setArrayPathValue(array);
    handleChange(array);
  };

  const deconstructString = (rawFieldText: string): FixedFieldState => {
    const [property, value] = rawFieldText.split(':');
    return { property, value };
  };

  const buildString = (field: FixedFieldState): string => {
    return `${field.property}:${field.value}`;
  };

  const handleChange = (array) => {
    if (onChange) {
      const strings = (array || []).map((field: FixedFieldState) => buildString(field));
      onChange(strings, arrayPath);
    }
  };

  useEffect(() => {
    setArrayPathValue((form.getInputProps(arrayPath).value ?? []).map((string) => deconstructString(string)));
  }, [form.getInputProps(arrayPath).value]);

  return (
    <>
      {arrayPathValue.map((field: FixedFieldState, rowIdx: number) => (
        <div key={`${arrayPathValue.length}-${rowIdx}`}>
          <Group align="end" p="md" w="100%">
            <Grid w="calc(100% - 90px)">
              <Grid.Col span={8}>
                <AsyncAutocomplete
                  label="Property"
                  data={propertyOptions || []}
                  value={field.property}
                  onChange={(value) => updateField(rowIdx, { property: value || '' })}
                  size="lg"
                  disabled={disabled}
                />
              </Grid.Col>
              <Grid.Col span={4}>
                <TextInput label="Value" value={field.value} onChange={(e) => updateField(rowIdx, { value: e.currentTarget.value })} disabled={disabled} />
              </Grid.Col>
            </Grid>
            {!disabled && (
              <UnstyledButton ml={10} w={50} h={50} type="button" onClick={() => removeRow(rowIdx)}>
                <img src={deleteIcon} alt="Remove" />
              </UnstyledButton>
            )}
          </Group>
          {rowIdx < arrayPathValue.length - 1 && <Divider size={0} w="100%" style={{ borderBottom: '1px solid #ccc' }} />}
        </div>
      ))}
      {!disabled && maxRows > arrayPathValue.length && (
        <Flex pl="md" pt="xs" justify={'flex-start'}>
          <Button type="button" onClick={addRow} variant="outline" size="lg">
            Add fixed field
          </Button>
        </Flex>
      )}
    </>
  );
};
